const MINUTE_MS = 60_000
const QUARTER_MS = 15 * MINUTE_MS

export type StepDirection = 'up' | 'down'

/**
 * One press of the stepper, in quarter-hour grid terms.
 *
 * Already on a quarter → move a full quarter. Off the grid → SNAP to the neighbouring quarter,
 * which is why `07:34 +` gives `07:45` rather than `07:49`. The system records to the minute;
 * a human correcting a memory works in quarters, and the snap is what makes the first press
 * tidy the value instead of carrying its untidiness along.
 */
export function quarterStep(iso: string, direction: StepDirection): string {
  const ms = new Date(iso).getTime()
  const onGrid = ms % QUARTER_MS === 0

  const next =
    direction === 'up'
      ? onGrid
        ? ms + QUARTER_MS
        : Math.ceil(ms / QUARTER_MS) * QUARTER_MS
      : onGrid
        ? ms - QUARTER_MS
        : Math.floor(ms / QUARTER_MS) * QUARTER_MS

  return new Date(next).toISOString()
}

/**
 * Whether a press is allowed, so the button can be disabled at the bound rather than silently
 * refusing. A correction can never leave lifecycle order or point at the future.
 */
export function canStep(
  iso: string,
  direction: StepDirection,
  bounds: { min?: string | null; max?: string | null },
): boolean {
  const candidate = new Date(quarterStep(iso, direction)).getTime()

  if (direction === 'down') {
    return bounds.min == null || candidate >= new Date(bounds.min).getTime()
  }
  return bounds.max == null || candidate <= new Date(bounds.max).getTime()
}

/** Minutes between two instants, for the live read-only duration under the steppers. */
export function minutesBetween(fromIso: string, toIso: string): number {
  return Math.max(
    0,
    Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / MINUTE_MS),
  )
}
